import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatMenuTrigger } from '@angular/material/menu';
import { ISkillResult } from 'src/app/interfaces/iskill-result';
import { ResultsSkillService } from 'src/app/services/learner/results-skill-service';
import { AppConstants } from 'src/app/app-constants';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ResponseTimeService } from 'src/app/services/general/response-time.service';
import { type QaiBadgeKind } from '@qai/ui';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';

const SkillKeys = ['Expert', 'Proficient', 'Developing'];
type SkillKey = (typeof SkillKeys)[number];

@Component({
  selector: 'app-results-skills',
  templateUrl: './results-skills.component.html',
  styleUrls: ['./results-skills.component.scss'],
  standalone: false,
  providers: [
      {
        provide: TRANSLOCO_SCOPE,
        useValue: 'results',
      },
    ],
})
export class ResultsSkillsComponent {
  @ViewChild(MatMenuTrigger) tooltipMenu!: MatMenuTrigger;
  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;
  @Input() practiceCallUuid: string = '';
  @Input() scoredAt: string = '';
  promptType: string = AppConstants.SCORE_SKILLS_PROMPT_TYPE;
  // @Output() resultAvailable = new EventEmitter(); /******* Not required temporarily *******/

  skillResults: ISkillResult | undefined;
  skillDetailsArray: any;
  isFetchingResults: boolean = false;
  timeInterval: boolean = false;
  resultReady: boolean = false;
  private pollingInterval: NodeJS.Timeout = setTimeout(() => {}, 0);
  msDuration: number = 90 * 1000;
  startTime: number = 0;
  isErrorLoadingScreen: boolean = false;
  displayButtonLoadingScreen: boolean = true;
  titleLoadingScreen: string = '';
  messageLoadingScreen: string = '';
  pastDate: boolean = false;
  translationLoaded: boolean = false;

  constructor(
    public dialog: MatDialog,
    private clipboard: Clipboard,
    private resultsSkillService: ResultsSkillService,
    private responseTimeService: ResponseTimeService,
    private translocoService: TranslocoService,
    private translationService: TranslationService

  ) {}

  ngOnInit() {
    this.translationService.loadScopedTranslation('results').then(() => {
      this.titleLoadingScreen = this.translocoService.translate('PROCESSING_SKILLS_FEEDBACK', {}, 'results');
      this.messageLoadingScreen =
      `${this.translocoService.translate('HANG_TIGHT', {}, 'results')} <br> ${this.translocoService.translate('VIEW_YOUR_RESULTS', {}, 'results')}`;
      this.translationLoaded = true;
    });
    this.pollData();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  stopPolling() {
    if (this.pastDate) {
      this.titleLoadingScreen = this.translocoService.translate(
        'SKILLS_SCORING_NOT_ENABLED',
        {},
        'results'
      );
      this.messageLoadingScreen = '';
      this.isErrorLoadingScreen = true;
      this.displayButtonLoadingScreen = true;
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  pollData() {
    this.getSkillResult();
    this.startTime = Date.now(); // Track the start time
    this.pollingInterval = setInterval(() => {
      const elapsedTime = Date.now() - this.startTime; // Calculate the elapsed time
      // After 1 minute we display longer than expected message
      if (elapsedTime >= 1 * this.msDuration) {
        this.titleLoadingScreen = this.translocoService.translate(
          'NEED_MORE_TIME',
          {},
          'results'
        );
        this.messageLoadingScreen = `${this.translocoService.translate(
          'REFRESH_INSTRUCTION',
          {},
          'results'
        )} <br> ${this.translocoService.translate(
          'RESULTS_IN_FEW_MINUTES',
          {},
          'results'
        )}`;
        this.isErrorLoadingScreen = true;
        this.displayButtonLoadingScreen = true;
        this.stopPolling(); // Stop the interval
        console.log('Polling stopped, display error message.');
      } else {
        if (!this.resultReady && !this.isFetchingResults) {
          this.getSkillResult();
        }
      }
    }, 5000);
  }

  checkPollingCompletion() {
    if (this.resultReady || this.isGreaterThan24HoursFromNow(this.scoredAt)) {
      this.stopPolling(); // Stop polling once result is ready
    }
  }

  getSkillResult() {
    this.isFetchingResults = true;
    this.responseTimeService
      .logResponseTime(
        this.resultsSkillService.getResultsSkillData(
          this.practiceCallUuid,
          this.promptType
        ),
        'getSkillResult'
      )
      .subscribe({
        next: (res: ISkillResult) => {
          this.isFetchingResults = false;
          this.skillResults = res;
          if (this.skillResults && this.skillResults?.contentDetails) {
            // this.resultAvailable.emit(true); /******* Not required temporarily *******/
            const contentDetails = JSON.parse(res.contentDetails);
            if (contentDetails && 'skilldetails' in contentDetails) {
              this.skillDetailsArray = contentDetails['skilldetails'];
            } else {
              this.skillDetailsArray = [];
            }
            if (this.skillDetailsArray.length === 0) {
              this.resultReady = false;
            } else {
              this.skillDetailsArray.forEach((skillDetail: any) => {
                skillDetail.showFeedback = false;
                skillDetail.actionItems = skillDetail['action items'];
                delete skillDetail['action items'];
                this.mapScoreToDescription(skillDetail);
              });
              this.sortSkills();
              this.resultReady = true;
            }
          }
          this.checkPollingCompletion();
        },
        error: (error) => {
          this.isFetchingResults = false;
          console.error('Error:', error);
          if (error.status == 500) {
            this.checkPollingCompletion();
            this.resultReady = false;
          }
        },
      });
  }

  sortSkills() {
    this.skillDetailsArray = this.skillDetailsArray.sort(
      (a: { score: string }, b: { score: string }) => {
        const scoreOrder = { Expert: 1, Proficient: 2, Developing: 3 };
        return (
          scoreOrder[a.score as keyof typeof scoreOrder] -
          scoreOrder[b.score as keyof typeof scoreOrder]
        );
      }
    );
  }

  isGreaterThan24HoursFromNow(dateString: string): boolean {
    const givenDate = new Date(dateString);
    const now = new Date(Date.now());

    // Calculate the absolute difference in milliseconds
    const differenceInMs = Math.abs(givenDate.getTime() - now.getTime());

    // Calculate the difference in hours
    const differenceInHours = differenceInMs / (1000 * 60 * 60);
    if (differenceInHours >= 24) {
      this.pastDate = true;
    } else {
      this.pastDate = false;
    }

    return this.pastDate;
  }
  openModal() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.hasBackdrop = true;
    dialogConfig.panelClass = 'custom-dialog-container';
    dialogConfig.enterAnimationDuration = 0;
    dialogConfig.exitAnimationDuration = '300ms';

    this.dialog.open(this.modalTemplate, dialogConfig);
  }

  closeModal() {
    this.dialog.closeAll();
  }

  mapScoreToDescription(skillDetail: { score: number | string }) {
    if (typeof skillDetail.score === 'number') {
      if (skillDetail.score >= 1 && skillDetail.score <= 2) {
        skillDetail.score = 'Developing';
      } else if (skillDetail.score >= 3 && skillDetail.score <= 4) {
        skillDetail.score = 'Proficient';
      } else if (skillDetail.score >= 5) {
        skillDetail.score = 'Expert';
      }
    }
  }

  protected skillKeys = SkillKeys;

  protected getScoreBadgeKind(value: SkillKey) {
    return {
      Developing: 'red',
      Proficient: 'yellow',
      Expert: 'green',
    }[value] as QaiBadgeKind;
  }
}
